import React from "react";
import styled from "styled-components";
import { CustomButton } from "@mindmemobile/mm-shared-global";
import { CustomLine } from "../styles";
import { ExportFormat } from "../utils/PaymentExportUtil";

type Props = {
  onExport: (format: ExportFormat) => void;
  /* True while the file is being fetched, so a second click cannot start a second export before
     the first has finished. */
  isExporting?: boolean;
};

type State = {
  isOpen: boolean;
};

const OPTIONS: { format: ExportFormat; label: string }[] = [
  { format: "csv", label: "CSV" },
  { format: "xlsx", label: "Excel (XLSX)" },
];

/* The menu carries its own styling instead of borrowing DashboardHeader's .add-new-dpdn rules, so
   it looks the same on the Money grids and inside the fundraiser tabs, which sit outside that
   header. The blue caret and border match the New button's menu. */
const Wrapper = styled.div`
  position: relative;
  display: inline-block;

  .export-menu {
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 12px;
    min-width: 180px;
    background: #fff;
    border: 1px solid #556bb8;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 1000;
    font-size: 15px;

    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 20px;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
    }
    &::before {
      border-bottom: 10px solid #556bb8;
      transform: translateY(-10px);
    }
    &::after {
      border-bottom: 10px solid #fff;
      transform: translateY(-8.5px);
    }
  }

  .export-item {
    padding: 10px 16px;
    color: #333;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background: #f4f7fb;
      color: #556bb8;
    }
  }
`;

/* "Export" split button for a grid's top-right bar. Built as its own small component rather than
   reusing NewItemDropDown, whose options are hard-wired to Contact / Payment / Invoice /
   Subscription - but it borrows the same button classes so the two read as a matching pair. */
class ExportDropDown extends React.Component<Props, State> {
  wrapperRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);
    this.state = { isOpen: false };
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  componentDidMount(): void {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount(): void {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  handleClickOutside(event: MouseEvent) {
    if (!this.wrapperRef.current?.contains(event.target as Node)) {
      this.setState({ isOpen: false });
    }
  }

  select(format: ExportFormat) {
    this.setState({ isOpen: false });
    this.props.onExport(format);
  }

  render() {
    const { isExporting = false } = this.props;
    return (
      <Wrapper ref={this.wrapperRef}>
        {/* Same colours/size as the New button beside it; CustomButton's defaults are the pale
            green status-chip look, which is why the button was barely visible without them. */}
        <CustomButton
          className="split-custom-btn"
          bgColor="#556bb8"
          color="#fff"
          height="40px"
          borderRadius={"4px !important"}
          fontSize={"17px"}
          disabled={isExporting}
          onClick={() => this.setState({ isOpen: !this.state.isOpen })}
        >
          {isExporting ? "Exporting..." : "Export"}
          <div className="split-wrapper">
            <CustomLine height="90%" bgColor="#fff" width="1px" />
            <i className="fa fa-solid fa-angle-down"></i>
          </div>
        </CustomButton>
        {this.state.isOpen && (
          <div className="export-menu">
            {OPTIONS.map((option) => (
              <div
                key={option.format}
                className="export-item"
                onClick={() => this.select(option.format)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </Wrapper>
    );
  }
}

export default ExportDropDown;
